import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import formatTime from "../../../utils/formatTime";

const STATUS_MESSAGES = {
  "not started": "AI transcription has not been started for this job.",
  "in progress": "AI transcription is in progress. Check back shortly.",
};

export default function AiTranscriptList({
  segments,
  isLoading,
  error,
  jobStatus,
  jobLang,
  errorMsg,
  onSegmentClick,
  activeStart,
}) {
  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Failed to copy");
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="w-6 h-6 border-4 border-t-transparent border-gray-300 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 px-2">
        <p className="text-sm text-red-500">Failed to load AI transcript.</p>
      </div>
    );
  }

  if (jobStatus === "failed") {
    return (
      <div className="text-center py-8 px-2">
        <p className="text-sm text-red-500">AI transcription failed.</p>
        {errorMsg ? (
          <p className="text-xs text-gray-500 dark:text-ramsalab-light-silver mt-2">
            {errorMsg}
          </p>
        ) : null}
      </div>
    );
  }

  if (jobStatus && jobStatus !== "completed") {
    return (
      <div className="text-center py-8 px-2">
        <p className="text-sm text-gray-500 dark:text-ramsalab-light-silver">
          {STATUS_MESSAGES[jobStatus] ?? `AI transcription status: ${jobStatus}.`}
        </p>
      </div>
    );
  }

  if (!segments?.length) {
    return (
      <div className="text-center py-8 px-2">
        <p className="text-sm text-gray-500 dark:text-ramsalab-light-silver">
          No AI transcript segments for this job.
        </p>
      </div>
    );
  }

  return (
    <div className="py-4">
      {jobLang ? (
        <p className="text-xs text-gray-500 dark:text-ramsalab-light-silver mb-3 px-1 select-none">
          Language: {jobLang}
        </p>
      ) : null}
      <div className="overflow-y-auto max-h-[calc(100vh-280px)] rounded border border-gray-200 dark:border-ramsalab-neutral-gray bg-gray-50 dark:bg-ramsalab-light-navy font-mono text-sm select-text">
        {segments.map((segment) => {
          const isActive = activeStart != null && segment.start === activeStart;
          const line = `[${formatTime(segment.start)}–${formatTime(segment.end)}] ${segment.text}`;
          return (
            <div
              key={segment.id}
              role="button"
              tabIndex={0}
              onClick={() => onSegmentClick(segment.start)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSegmentClick(segment.start);
                }
              }}
              className={`flex items-start gap-2 px-2 py-1 cursor-pointer border-l-4 whitespace-pre-wrap break-words text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-ramsalab-neutral-gray/30 ${
                isActive
                  ? "border-l-ramsalab-primary bg-ramsalab-primary/10"
                  : "border-l-transparent"
              }`}
            >
              <span className="flex-1 min-w-0">{line}</span>
              {isActive ? (
                <button
                  type="button"
                  aria-label="Copy transcript text"
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleCopy(segment.text);
                  }}
                  className="shrink-0 p-0.5 rounded text-gray-500 hover:text-gray-800 dark:hover:text-white"
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
